import FeatureRoute from '@components/FeatureRoute'
import { MembershipLayout } from '@layouts/membership/MembershipLayout'
import AnalyticsPage from '@pages/membership/AnalyticsPage'
import IncomingRenewalsPage from '@pages/membership/analytics/IncomingRenewalsPage'
import TransactionsPage from '@pages/membership/analytics/TransactionsPage'
import RevenuePage from '@pages/membership/RevenuePage'
import CouponsPage from '@pages/membership/coupons/CouponsPage'
import EventsCollectionPage from '@pages/membership/EventsCollectionPage'
import PerksPage from '@pages/membership/PerksPage'
import RequestsPage from '@pages/membership/RequestsPage'
import { Navigate, RouteObject, useLocation } from 'react-router-dom'
import { PropsWithChildren } from 'react'
import eventRoutes from './EventsRoutes'
import membersRoutes from './MembersRoutes'

/** Redirects to `to`, keeping the query string (filters of the old URLs). */
const RedirectKeepQuery = ({ to }: { to: string }) => {
  const { search } = useLocation()
  return <Navigate to={`${to}${search}`} replace />
}

/**
 * Revenue pages need the `membershipAnalyticsOverview` flag; the permission is
 * declared in `ROUTE_PERMISSIONS` (`/membership/revenue` subtree), which
 * `<RouteGate />` enforces above every logged route.
 */
const RevenueRoute = ({ children }: PropsWithChildren) => (
  <FeatureRoute flag="membershipAnalyticsOverview" redirectTo="/404">
    {children}
  </FeatureRoute>
)

const membershipRoutes: RouteObject[] = [
  {
    element: <MembershipLayout />,
    children: [
      { path: 'requests', element: <RequestsPage /> },
      { path: 'coupons', element: <CouponsPage /> },
      { path: 'analytics', element: <AnalyticsPage /> },
      {
        path: 'revenue',
        element: (
          <RevenueRoute>
            <RevenuePage />
          </RevenueRoute>
        ),
      },
      {
        path: 'revenue/transactions',
        element: (
          <RevenueRoute>
            <TransactionsPage />
          </RevenueRoute>
        ),
      },
      {
        path: 'revenue/renewals',
        element: (
          <RevenueRoute>
            <IncomingRenewalsPage />
          </RevenueRoute>
        ),
      },
      // Legacy URLs (bookmarks, old links): same page, query string kept.
      {
        path: 'analytics/transactions',
        element: <RedirectKeepQuery to="/membership/revenue/transactions" />,
      },
      {
        path: 'analytics/incoming-renewal',
        element: <RedirectKeepQuery to="/membership/revenue/renewals" />,
      },
      { path: 'benefits', element: <PerksPage /> },
      { path: 'events-collections', element: <EventsCollectionPage /> },
      { index: true, element: <Navigate to="requests" replace /> },
      { path: '*', element: <Navigate to="/404" replace /> },
    ],
  },
  { path: 'members', children: membersRoutes },
  { path: 'events', children: eventRoutes },
]

export default membershipRoutes
